import React, { useState, useEffect, useCallback } from "react";
import { Modal, Button } from "react-bootstrap";
import { FaChevronLeft, FaChevronRight, FaTimes } from "react-icons/fa";

const FullscreenGallery = ({ images, isOpen, onClose, initialIndex = 0 }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex, isOpen]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prevIndex) =>
      prevIndex === images.length - 1 ? 0 : prevIndex + 1,
    );
  }, [images.length]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1,
    );
  }, [images.length]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      switch (e.key) {
        case "ArrowLeft":
          goToPrevious();
          break;
        case "ArrowRight":
          goToNext();
          break;
        case "Escape":
          onClose();
          break;
        default:
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, goToNext, goToPrevious, onClose]);

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrevious();
    }
  };

  if (!images || images.length === 0) return null;

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      fullscreen
      className="fullscreen-gallery-modal"
      backdrop="static"
    >
      <Modal.Body className="p-0 d-flex align-items-center justify-content-center bg-dark position-relative">
        {/* Close button */}
        <Button
          variant="outline-light"
          className="position-absolute top-0 end-0 m-3"
          style={{ zIndex: 1050 }}
          onClick={onClose}
        >
          <FaTimes size={window.innerWidth <= 768 ? 20 : 24} />
        </Button>

        {/* Previous button */}
        <Button
          variant="outline-light"
          className="position-absolute start-0 top-50 translate-middle-y ms-3"
          style={{ zIndex: 1050 }}
          onClick={goToPrevious}
          disabled={images.length <= 1}
        >
          <FaChevronLeft size={window.innerWidth <= 768 ? 24 : 24} />
        </Button>

        {/* Image container */}
        <div
          className="fullscreen-gallery-image-wrap"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <img
            src={`/images/carousel/webp/${images[currentIndex].imageName}`}
            alt={images[currentIndex].caption}
            className={`fullscreen-gallery-image${
              images[currentIndex].mobileCrop ? " gallery-image-mobile-crop" : ""
            }`}
            draggable={false}
          />
        </div>

        {/* Next button */}
        <Button
          variant="outline-light"
          className="position-absolute end-0 top-50 translate-middle-y me-3"
          style={{ zIndex: 1050 }}
          onClick={goToNext}
          disabled={images.length <= 1}
        >
          <FaChevronRight size={window.innerWidth <= 768 ? 24 : 24} />
        </Button>

        {/* Image caption */}
        {images[currentIndex].caption && (
          <div
            className="position-absolute bottom-0 start-0 end-0 text-center text-white p-3"
            style={{
              background: "linear-gradient(transparent, rgba(0, 0, 0, 0.7))",
              zIndex: 1040,
            }}
          >
            <h4
              className="mb-0"
              style={{
                fontSize: window.innerWidth <= 768 ? "1.2rem" : "1.5rem",
              }}
            >
              {images[currentIndex].caption}
            </h4>
          </div>
        )}

        {/* Image counter */}
        <div
          className="position-absolute top-0 start-50 translate-middle-x mt-3 text-white"
          style={{ zIndex: 1050 }}
        >
          <small
            style={{
              fontSize: window.innerWidth <= 768 ? "0.8rem" : "0.9rem",
            }}
          >
            {currentIndex + 1} of {images.length}
          </small>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default FullscreenGallery;
